import { useMsalAuthentication, useMsal } from "@azure/msal-react";
import {
  InteractionType,
  InteractionStatus,
  AccountInfo,
} from "@azure/msal-browser";
import { loginRequest, tokenRequest } from "../config/authConfig";
// import { useAppContext } from "../hooks/context/AppContext";
import { useEffect } from "react";
// import useLocalStorage from "../hooks/useLocalStorage";

interface SSOAuthenticatorProps {
  children: React.ReactNode;
}

const SSOAuthenticator: React.FC<SSOAuthenticatorProps> = ({ children }) => {
  // const [authData, setAuthData] = useLocalStorage("auth_data", null);
  const { login, error } = useMsalAuthentication(
    InteractionType.Silent,
    loginRequest
  );
  const { accounts, inProgress, instance } = useMsal();
  // const { updateAuthData } = useAppContext();

  // Function to update auth data and handle token refresh
  const updateAuthContext = async (account: AccountInfo) => {
    try {
      // Get fresh token silently
      const tokenResponse = await instance.acquireTokenSilent({
        ...tokenRequest,
        account: account,
      });

      return tokenResponse;
    } catch (silentError) {
      console.error("Silent token acquisition failed:", silentError);
      throw silentError;
    }
  };

  useEffect(() => {
    if (!accounts.length || inProgress !== InteractionStatus.None) return;

    const account = accounts[0];
    let refreshTimeout: NodeJS.Timeout;
    const setupTokenRefresh = async () => {
      try {
        const tokenResponse = await updateAuthContext(account);
        if (tokenResponse?.expiresOn) {
          // Refresh token 60 minutes before expiration
          const expiresIn =
            tokenResponse.expiresOn.getTime() - Date.now() - 60000;
          if (expiresIn > 0) {
            refreshTimeout = setTimeout(() => {
              setupTokenRefresh();
            }, expiresIn);
          }
        }
      } catch (error) {
        console.error("Token refresh setup failed:", error);
      }
    };

    // Initial setup
    setupTokenRefresh();

    // Event listener for token expiration
    const callbackId = instance.addEventCallback((event) => {
      if (event.eventType === "msal:acquireTokenFailure") {
        setupTokenRefresh().catch((e) => {
          console.error("Token refresh failed:", e);
        });
      }
    });

    return () => {
      if (refreshTimeout) clearTimeout(refreshTimeout);
      if (callbackId) instance.removeEventCallback(callbackId);
    };
  }, [accounts, inProgress, instance]);

  // Handle initial authentication and errors
  useEffect(() => {
    if (error) {
      //('Silent login failed, attempting redirect...');
      login(InteractionType.Redirect, loginRequest).catch((e) => {
        console.error("Login redirect failed:", e);
      });
    }
  }, [error, login]);

  // Update auth data when account changes
  useEffect(() => {
    if (accounts.length > 0 && inProgress === InteractionStatus.None) {
      const account = accounts[0];
      updateAuthContext(account).catch((e) => {
        console.error("Initial auth context update failed:", e);
      });
    }
  }, [accounts, inProgress]);

  if (accounts.length > 0 && inProgress === InteractionStatus.None) {
    return <>{children}</>;
  }

  return null;
};

export default SSOAuthenticator;
